import { filter, interval, map, switchMap, takeWhile, tap } from 'rxjs'
import { action, computed, observable } from 'mobx'
import { jwtDecode } from 'jwt-decode'
import type { AlertFactory } from '@/features/shell'
import { EmailConfirmationApi } from '@/features/email-confirmation/api/email-confirmation-api'
import type { Deactivatable } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'

/**
 * Payload for the session token.
 */
interface SessionTokenPayload {
  iat: number
  exp: number
  email: string
}

/**
 * Confirm Email View Model.
 */
export class ConfirmEmailViewModel implements Deactivatable {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _api = new EmailConfirmationApi()
  private _token: string | null = null

  @observable private accessor _tokenPayload: SessionTokenPayload | null = null

  /**
   * Indicates whether the user's email has been confirmed.
   */
  @observable
  accessor emailConfirmed = false

  /**
   * Indicates whether the confirmation attempt has expired based on the token.
   */
  @observable
  accessor pageExpired = false

  /**
   * Fires when navigation to the email confirmed page should occur.
   */
  readonly navigateToEmailConfirmed$ = toStream(() => this.emailConfirmed).pipe(
    filter(Boolean),
  )

  /**
   * 5 second ticker, once the token payload is known.
   */
  private readonly _timer = toStream(() => this._tokenPayload, true).pipe(
    filter(Boolean),
    map((payload) => payload.exp * 1000),
    switchMap((expiresAt) =>
      interval(5000).pipe(
        takeWhile(() => !this.emailConfirmed && !this.pageExpired),
        tap(() => {
          this._checkTokenExpiration(expiresAt)
          this._checkVerificationStatus()
        }),
      ),
    ),
  )

  constructor(private readonly _newAlert: AlertFactory) {
    this._lifecycle.manage(this._timer)
  }

  /**
   * The user's email, based on the token.
   */
  @computed
  get email() {
    return this._tokenPayload?.email ?? ''
  }

  /**
   * Activates the view model.
   */
  @action.bound
  activate(token: string) {
    this._lifecycle.activate()
    this._token = token

    const { email, exp, iat = 0 } = decodeToken(token)
    if (!email || !exp) {
      this._newAlert().saying('Invalid token').withState('error').show()
      return
    }

    this._tokenPayload = { email, exp, iat }

    // Check token expiration immediately upon activation as well.
    this._checkTokenExpiration(exp * 1000)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Resends the verification email.
   */
  @action.bound
  async resendVerificationEmail() {
    if (!this._token) {
      return
    }

    await this._api
      .resendEmail(this._token)
      .catch(() =>
        this._newAlert().saying('An error occurred').withState('error').show(),
      )
  }

  /**
   * Checks the email verification status of the user. Used to automatically update the page.
   */
  private _checkVerificationStatus() {
    if (!this._token) {
      return
    }

    this._api.getVerificationStatus(this._token).then(
      action((status) => {
        this.emailConfirmed = status
      }),
      // The next tick asks again.
      () => undefined,
    )
  }

  /**
   * Checks whether the token has expired.
   */
  @action.bound
  private _checkTokenExpiration(expiresAt: number) {
    if (Date.now() > expiresAt) {
      this.pageExpired = true
    }
  }
}

/**
 * Decodes the provided token; a malformed token yields an empty payload.
 */
function decodeToken(token: string): Partial<SessionTokenPayload> {
  try {
    return jwtDecode<SessionTokenPayload>(token)
  } catch {
    return {}
  }
}
